<template>
    <modal-dialog
        v-if="!!project"
        id="break-into-scopes-dialog"
        :show.sync="shown"
        class="uk-modal-dialog-large"
    >
        <template v-slot:title>
            Scopes for project {{ project.number }}
            <div
                :class="{ 'uk-text-danger': project.computed_fee != grand_total }"
                class="uk-text-small"
            >
                Proposal amount to match: $&nbsp;{{ project.computed_fee | nibnut.number("0,0") }}
            </div>
        </template>

        <div
            v-for="(scope, index) in scopes"
            :key="index"
        >
            <table
                class="uk-table new-project-scopes"
            >
                <thead>
                    <tr>
                        <td colspan="3">
                            <div
                                :class="{ 'uk-invisible': !index }"
                                class="lam-new-scope-delete uk-margin-small-right"
                            >
                                <base-button
                                    color="danger"
                                    size="mini"
                                    @click.prevent="remove_scope(index)"
                                >
                                    <open-icon glyph="trash" />
                                </base-button>
                            </div>
                            <div class="uk-flex uk-flex-center uk-flex-middle">
                                <div
                                    class="uk-flex-item-none uk-margin-small-right"
                                >
                                    {{ `${index}`.padStart(2, "0") }}
                                </div>
                                <div class="uk-flex-item-1 uk-margin-small-right">
                                    <form-input
                                        :id="`scope-${index}-name`"
                                        v-model="scope.name"
                                        name="name"
                                        :required="false"
                                    />
                                </div>
                                <div class="uk-flex-item-none">
                                    <form-input
                                        :id="`scope-${index}-fee`"
                                        v-model="scope.fee"
                                        type="float"
                                        name="fee"
                                        glyph="dollar"
                                        :required="true"
                                        align="right"
                                        :error="!!scope.fee && (scope_total(scope) !== parseFloat(scope.fee))"
                                    />
                                </div>
                            </div>
                        </td>
                    </tr>
                </thead>
                <draggable
                    ref="tbody"
                    :id="`scope-${index}`"
                    :list="scope.phases"
                    tag="tbody"
                    group="phases"
                    :sort="false"
                    handle=".lam-drag-handle"
                    draggable="tr"
                    ghostClass="lam-dropzone"
                    dragClass="lam-dragging"
                    @end="auto_totals"
                >
                    <tr
                        v-for="phase in scope.phases"
                        :key="phase.id"
                        class="lam-hoverable"
                    >
                        <td>
                            <div class="uk-flex">
                                <div
                                    class="lam-drag-handle uk-margin-small-right"
                                >
                                    <open-icon glyph="arrows-v" />
                                </div>
                                <div class="uk-flex-item-1">
                                    {{ phase_standardized_title(phase) }}<span v-if="!!phase.deltek_id"> ({{ phase.deltek_id }})</span>
                                </div>
                            </div>
                        </td>
                        <td class="uk-text-center">
                            {{ phase.computed_starts_at | nibnut.date }} to {{ phase.computed_ends_at | nibnut.date }}
                        </td>
                        <td class="uk-text-right">
                            $&nbsp;{{ phase.fee | nibnut.number("0,0") }}
                        </td>
                    </tr>
                </draggable>
            </table>
        </div>

        <div class="uk-text-center">
            <a href class="uk-button uk-button-mini uk-button-link" @click.prevent.stop="add_scope">
                <open-icon glyph="plus" /> Add a new scope
            </a>
        </div>

        <div class="uk-modal-footer uk-text-center">
            <base-button
                size="large"
                class="uk-margin-small-right"
                @click.native.prevent="shown = false"
            >
                Cancel
            </base-button>
            <base-button
                :waiting="saving"
                size="large"
                :disabled="project.computed_fee != grand_total"
                class="uk-margin-small-left"
                @click.native.prevent="save"
            >
                Save
            </base-button>
        </div>
    </modal-dialog>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import phase_utilities from "../../mixins/PhaseUtilities"

import ModalDialog from "../../dialogs/ModalDialog/ModalDialog"
import FormInput from "../../ui/FormInput"
import BaseButton from "../../ui/BaseButton"
import OpenIcon from "../../ui/OpenIcon"
import draggable from "vuedraggable"

export default {
    mixins: [phase_utilities],
    components: {
        ModalDialog,
        FormInput,
        BaseButton,
        OpenIcon,
        draggable
    },
    mounted () {
        this.reset()
    },
    watch: {
        show: "reset",
        "project.id": "reset"
    },
    methods: {
        reset () {
            if(this.show && this.project && this.project.id) {
                // load latest proposal for total amount ; phases have been loaded by editor already
                this.scopes = [
                    {
                        name: this.project.nickname || "",
                        fee: this.project.computed_fee,
                        phases: this.raw_phase_records.map(phase => {
                            return { ...phase }
                        })
                    }
                ]
                this.add_scope()
            }
        },
        scope_total (scope) {
            let total = 0
            if(!!scope && !!scope.phases && !!scope.phases.length) {
                total = scope.phases.reduce((total, phase) => {
                    return total + phase.fee
                }, 0)
            }
            return Math.round(total)
        },
        auto_totals () {
            this.scopes.forEach(scope => {
                scope.fee = scope.phases.reduce((total, phase) => {
                    return total + phase.fee
                }, 0)
            })
        },
        add_scope () {
            this.scopes.push({ name: "", fee: 0, phases: [] })
        },
        remove_scope (index) {
            if(index) {
                this.scopes[index].phases.map(phase => this.scopes[0].phases.push(phase))
                this.scopes.splice(index, 1)
            }
        },
        save () {
            const data = {
                fields: this.fields,
                relation_ids: this.relationIds,
                scopes: []
            }
            this.scopes.map(scope => {
                data.scopes.push({
                    name: scope.name,
                    fee: parseFloat(scope.fee) || 0,
                    phases: scope.phases.map(phase => phase.id)
                })
            })
            this.saving = true
            this.$store.dispatch("RECORD_ACTION", {
                entity: "project",
                id: this.project.id,
                action: "break_into_scopes",
                data,
                method: "post"
            }).then(() => {
                this.shown = false
                this.$emit("scoped")
            }).catch(error => {
                this.$error(error.message)
            }).then(() => {
                this.save = false
            })
        },
        close () {
            this.$emit("update:show", false)
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        state_identifier () {
            return "break-into-scopes-dialog"
        },
        shown: {
            get: function () {
                return this.show
            },
            set: function (show) {
                if(!show) this.close()
            }
        },
        raw_phase_records () {
            if(!this.project || !this.project.id) return []
            return this.entity_records("phase").filter(phase => phase.project_id === this.project.id)
        },
        grand_total () {
            let total = 0
            if(this.scopes) {
                total = this.scopes.reduce((total, scope) => {
                    return total + parseFloat(scope.fee)
                }, 0)
            }
            return Math.round(total)
        }
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        project: {
            type: Object,
            required: true
        },
        fields: {
            type: Array,
            required: true
        },
        relationIds: {
            type: Array,
            required: true
        }
    },
    data () {
        return {
            scopes: [],
            saving: false
        }
    }
}
</script>
